
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Instagram, Globe, Loader2 } from 'lucide-react';
import { loginWithGoogle } from '../services/authService';
import { UserProfile } from '../types';

interface LoginViewProps {
  onLoginSuccess: (user: UserProfile) => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getErrorMessage = (err: any) => {
    const code = err?.code;
    switch (code) {
      case 'auth/popup-closed-by-user': return null;
      case 'auth/popup-blocked': return 'El navegador bloqueó la ventana. Permitir popups e intentar nuevamente.';
      case 'auth/too-many-requests': return 'Demasiados intentos fallidos. Por favor espera unos minutos.';
      default: return 'Ocurrió un error inesperado. Por favor intenta nuevamente.';
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const user = await loginWithGoogle();
      onLoginSuccess(user);
    } catch (err: any) {
      console.error("Google Login failed", err);
      setIsLoading(false);
      const errorMsg = getErrorMessage(err);
      if (errorMsg) {
        setError(errorMsg);
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] w-full px-4 animate-in fade-in zoom-in duration-500">
      
      {/* Main Card */}
      <div className="w-full max-w-md bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden group">
        
        {/* Glow Effects */}
        <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Free Badge */}
        <div className="absolute top-6 right-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-widest">
                100% Gratuito
            </div>
        </div>

        {/* Header Section */}
        <div className="flex flex-col items-center mb-8 mt-2">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.4)] mb-4 transform rotate-3 overflow-hidden">
            <img src="/lps-logo-blue.png" alt="Legal Prompt Studio" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">LegalPromptStudio</h1>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500 font-medium tracking-wide">
             <span>by</span>
             <a href="https://inteligencialegal.com.ar" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-white transition-colors font-bold">InteligenciaLegal</a>
          </div>
        </div>

        {/* Auth Content */}
        <div className="space-y-6">
          
          {/* Error Alert */}
          {error && (
            <div className="p-3 bg-red-950/40 border border-red-900/50 rounded-lg flex items-start gap-2 text-red-200 text-sm animate-in slide-in-from-top-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {/* Google Button - styled for dark theme */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-semibold py-3.5 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed border border-zinc-700 hover:border-zinc-600 shadow-lg shadow-black/20 hover:shadow-cyan-500/10 group/google"
          >
             {isLoading ? (
                 <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
             ) : (
                <>
                    <div className="p-1.5 bg-white rounded-lg">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    </div>
                    <span className="group-hover/google:text-cyan-400 transition-colors">Continuar con Google</span>
                </>
             )}
          </button>

          <p className="text-center text-xs text-zinc-500">
            Al continuar, aceptás nuestros{' '}
            <Link 
              to="/terms"
              className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
            >
              términos y condiciones
            </Link>
            {' '}y{' '}
            <Link 
              to="/privacy"
              className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
            >
              política de privacidad
            </Link>.
          </p>

        </div>

        {/* Footer Links */}
        <div className="mt-8 pt-6 border-t border-zinc-800/50 flex flex-col items-center gap-4">
          {/* Legal Mini Links */}
          <div className="flex items-center gap-3 text-[10px] text-zinc-600">
            <Link 
              to="/terms"
              className="hover:text-zinc-400 transition-colors"
            >
              Términos y Condiciones
            </Link>
            <span className="text-zinc-800">|</span>
            <Link 
              to="/privacy"
              className="hover:text-zinc-400 transition-colors"
            >
              Política de Privacidad
            </Link>
          </div>
          
          {/* Social Links */}
          <div className="flex gap-6">
                 <a href="https://inteligencialegal.com.ar" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors" title="Website">
                    <Globe className="w-4 h-4" />
                 </a>
                 <a href="https://instagram.com/inteligencialegal.ok" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-pink-500 transition-colors" title="Instagram">
                    <Instagram className="w-4 h-4" />
                 </a>
          </div>
          <p className="text-[10px] text-zinc-700">
            © {new Date().getFullYear()} InteligenciaLegal
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
